'use client';

import { normalizeSvgFileForUpload } from '@/utils/normalizeSvgUpload';
import {
    CheckIcon,
    PencilSquareIcon,
    SparklesIcon,
    XMarkIcon,
} from '@heroicons/react/20/solid';
import { useEffect, useMemo, useRef, useState } from 'react';
import { EditorDrawer } from '../EditorDrawer';
import { useEditor } from '../context/EditorContext/EditorContext';
import { useNotifications } from '../context/NotificationContext/NotificationContext';
import {
    applyAltTextDrafts,
    applyImageDeletions,
    applyImageReplacements,
} from '../helpers/altTextAudit';
import {
    getAltTextQualityIssues,
    normalizeAltText,
} from '../helpers/altTextQuality';
import {
    LARGE_IMAGE_SIZE_LIMIT,
    getImageIssuePriority,
    getImageSizeBadgeState,
} from '../helpers/imageSize';
import { renameImageAsset } from '../helpers/imageRename';
import { API, APIRequestError } from '../utils/api';

const formatPageName = (pageName) => {
    const value = String(pageName || 'current');
    if (value === 'index') return 'Home';
    return value
        .replace(/[-_]+/g, ' ')
        .replace(/\b\w/g, (character) => character.toUpperCase());
};

const getUsageContexts = (image) =>
    Array.from(
        new Set(
            (image.usages || []).map(
                (usage) =>
                    `${formatPageName(usage.pageName)} · ${
                        usage.componentName || usage.file || 'Component'
                    }`
            )
        )
    );

const splitFilenameParts = (filename) => {
    const trimmed = String(filename || '').trim();
    if (!trimmed) return { name: '', extension: '' };
    const lastDot = trimmed.lastIndexOf('.');
    if (lastDot <= 0) return { name: trimmed, extension: '' };
    return {
        name: trimmed.slice(0, lastDot),
        extension: trimmed.slice(lastDot),
    };
};

const buildFilename = (name, extension) => {
    const normalizedName = String(name || '')
        .trim()
        .replace(/[\\/]+/g, '')
        .replace(/\s+/g, '-');
    return normalizedName ? `${normalizedName}${extension}` : '';
};

export function AltTextEditor({
    images,
    imageSizesByUrl,
    sources,
    focusMode,
    open,
    onClose,
    onSourcesSaved,
    onImageSizeChange,
}) {
    const editor = useEditor();
    const notifications = useNotifications();
    const [drafts, setDrafts] = useState({});
    const [decorativeDrafts, setDecorativeDrafts] = useState({});
    const [replacements, setReplacements] = useState({});
    const [deletedImageIds, setDeletedImageIds] = useState([]);
    const [filenameDrafts, setFilenameDrafts] = useState({});
    const [editingFilenameId, setEditingFilenameId] = useState(null);
    const [filenameInput, setFilenameInput] = useState('');
    const [uploadingImageId, setUploadingImageId] = useState(null);
    const [generatingImageIds, setGeneratingImageIds] = useState([]);
    const [isGeneratingAllAltText, setIsGeneratingAllAltText] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const wasOpenRef = useRef(false);
    const firstHeavyImageRef = useRef(null);

    useEffect(() => {
        if (open && !wasOpenRef.current) {
            setDrafts(
                Object.fromEntries(
                    images.map((image) => [
                        image.id,
                        image.missingAltText ? '' : image.altText || '',
                    ])
                )
            );
            setDecorativeDrafts(
                Object.fromEntries(
                    images.map((image) => [image.id, Boolean(image.decorative)])
                )
            );
            setReplacements({});
            setDeletedImageIds([]);
            setFilenameDrafts(
                Object.fromEntries(
                    images.map((image) => [image.id, image.label])
                )
            );
            setEditingFilenameId(null);
            setFilenameInput('');
        }
        wasOpenRef.current = open;
    }, [images, open]);

    const activeImages = useMemo(() => {
        const deletedIds = new Set(deletedImageIds);
        return images.filter((image) => !deletedIds.has(image.id));
    }, [deletedImageIds, images]);

    const imageStatusById = useMemo(() => {
        const baseStatuses = images.map((image) => {
            const value = drafts[image.id] ?? image.altText ?? '';
            const decorative = Boolean(
                decorativeDrafts[image.id] ?? image.decorative
            );
            const replacement = replacements[image.id];
            const sizeBytes =
                replacement?.sizeBytes ?? imageSizesByUrl[image.url] ?? 0;
            return { image, value, decorative, sizeBytes };
        });
        const duplicateCounts = baseStatuses.reduce((counts, status) => {
            const normalized = status.decorative
                ? ''
                : normalizeAltText(status.value);
            if (normalized) {
                counts.set(normalized, (counts.get(normalized) || 0) + 1);
            }
            return counts;
        }, new Map());

        return new Map(
            baseStatuses.map((status) => {
                const normalized = normalizeAltText(status.value);
                const qualityIssues = status.decorative
                    ? []
                    : getAltTextQualityIssues({
                          altText: status.value,
                          label:
                              filenameDrafts[status.image.id] ||
                              status.image.label,
                          duplicateCount: duplicateCounts.get(normalized) || 0,
                      });
                const isMissing =
                    !status.decorative && !String(status.value).trim();
                const isHeavy =
                    Number(status.sizeBytes) > LARGE_IMAGE_SIZE_LIMIT;
                return [
                    status.image.id,
                    {
                        ...status,
                        isMissing,
                        isHeavy,
                        qualityIssues,
                    },
                ];
            })
        );
    }, [
        decorativeDrafts,
        drafts,
        filenameDrafts,
        imageSizesByUrl,
        images,
        replacements,
    ]);

    const displayImages = useMemo(() => {
        return activeImages
            .map((image, index) => {
                const status = imageStatusById.get(image.id);
                return {
                    image,
                    index,
                    priority: getImageIssuePriority({
                        missingAltText: status?.isMissing,
                        sizeBytes: status?.sizeBytes,
                        hasQualityIssues: status?.qualityIssues?.length > 0,
                    }),
                };
            })
            .sort(
                (left, right) =>
                    left.priority - right.priority || left.index - right.index
            )
            .map(({ image }) => image);
    }, [activeImages, imageStatusById]);

    useEffect(() => {
        if (!open || focusMode !== 'heavy') return undefined;
        const timeoutId = window.setTimeout(() => {
            firstHeavyImageRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }, 150);
        return () => window.clearTimeout(timeoutId);
    }, [displayImages, focusMode, open]);

    const applyAltText = (image, value) => {
        setDrafts((current) => ({ ...current, [image.id]: value }));
    };

    const setImageDecorative = (image, decorative) => {
        setDecorativeDrafts((current) => ({
            ...current,
            [image.id]: decorative,
        }));
        if (decorative) {
            setDrafts((current) => ({ ...current, [image.id]: '' }));
        }
    };

    const requestAltText = async (image) => {
        const sourceUrl = replacements[image.id]?.url || image.url;
        const imageUrl = sourceUrl.startsWith('data:')
            ? sourceUrl
            : new URL(sourceUrl, window.location.origin).toString();
        const data = await API.post(
            `${editor.global.data.company_id}/alt-text/generate`,
            { imageUrl }
        );
        const altText = String(data?.alt_text || data?.altText || '')
            .trim()
            .replace(/^["']+|["']+$/g, '');
        if (!altText) throw new Error('No alt text returned');
        return altText;
    };

    const handleGenerateAltText = async (image) => {
        if (generatingImageIds.includes(image.id)) return;

        setGeneratingImageIds((current) => [...current, image.id]);
        try {
            const altText = await requestAltText(image);
            applyAltText(image, altText);
        } catch (error) {
            notifications.push({
                title: 'Failed to generate alt text',
                message:
                    error instanceof Error ? error.message : 'Unknown error',
                variant: 'error',
                ttl: 20,
                error,
            });
        } finally {
            setGeneratingImageIds((current) =>
                current.filter((imageId) => imageId !== image.id)
            );
        }
    };

    const handleGenerateAllAltText = async () => {
        if (isGeneratingAllAltText || generatingImageIds.length > 0) return;

        const eligibleImages = activeImages.filter(
            (image) => !(decorativeDrafts[image.id] ?? image.decorative)
        );
        if (!eligibleImages.length) {
            notifications.push({
                title: 'No alt text to generate',
                message: 'All images are marked as decorative.',
                variant: 'info',
            });
            return;
        }

        setIsGeneratingAllAltText(true);
        setGeneratingImageIds(eligibleImages.map((image) => image.id));

        let nextIndex = 0;
        const generatedDrafts = {};
        const failures = [];
        const worker = async () => {
            while (nextIndex < eligibleImages.length) {
                const image = eligibleImages[nextIndex];
                nextIndex += 1;
                try {
                    generatedDrafts[image.id] = await requestAltText(image);
                } catch (error) {
                    failures.push({ image, error });
                } finally {
                    setGeneratingImageIds((current) =>
                        current.filter((imageId) => imageId !== image.id)
                    );
                }
            }
        };

        try {
            await Promise.all(
                Array.from(
                    { length: Math.min(3, eligibleImages.length) },
                    worker
                )
            );
            setDrafts((current) => ({ ...current, ...generatedDrafts }));

            const generatedCount = Object.keys(generatedDrafts).length;
            if (generatedCount > 0) {
                notifications.push({
                    title: `Generated alt text for ${generatedCount} ${
                        generatedCount === 1 ? 'image' : 'images'
                    }`,
                    message:
                        failures.length > 0
                            ? `${failures.length} ${
                                  failures.length === 1 ? 'image' : 'images'
                              } could not be generated.`
                            : 'Review the descriptions, then save your changes.',
                    variant: failures.length > 0 ? 'warning' : 'success',
                    ttl: 12,
                });
            } else {
                notifications.push({
                    title: 'Failed to generate alt text',
                    message: `Alt text could not be generated for ${
                        failures.length
                    } ${failures.length === 1 ? 'image' : 'images'}.`,
                    variant: 'error',
                    ttl: 20,
                    error: failures[0]?.error,
                });
            }
        } finally {
            setGeneratingImageIds([]);
            setIsGeneratingAllAltText(false);
        }
    };

    const handleCancel = () => onClose();

    const handleReplaceImage = async (image, event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setUploadingImageId(image.id);
        try {
            const normalizedImage = await normalizeSvgFileForUpload(file);
            const formData = new FormData();
            formData.append('image', normalizedImage);
            let filename = file.name;
            try {
                filename =
                    decodeURIComponent(
                        new URL(image.url, window.location.origin).pathname
                            .split('/')
                            .filter(Boolean)
                            .pop()
                    ) || file.name;
            } catch {
                // Use the selected filename when the existing URL is invalid.
            }
            const uploadUrl =
                `https://image-search-ytnjkd5upa-uc.a.run.app/upload/image/${editor.global.data.company_id}?` +
                new URLSearchParams({ filename });
            const response = await fetch(uploadUrl, {
                method: 'POST',
                body: formData,
            });
            if (!response.ok) {
                throw new Error(`Image upload failed: ${response.statusText}`);
            }
            const data = await response.json();
            const replacement = {
                ...data,
                url: data.url || image.url,
                sizeBytes: file.size,
            };
            setReplacements((current) => ({
                ...current,
                [image.id]: replacement,
            }));

            const libraryImage = editor.site.data?.images?.find(
                (candidate) => String(candidate?.url || '').trim() === image.url
            );
            if (libraryImage?.image_id) {
                editor.site.updateImage?.({
                    image_id: libraryImage.image_id,
                    data: { ...data, size_bytes: file.size },
                });
            } else {
                editor.site.addImage?.({ ...data, size_bytes: file.size });
            }
        } catch (error) {
            notifications.push({
                title: 'Image replacement failed',
                variant: 'error',
                error,
            });
        } finally {
            setUploadingImageId(null);
            event.target.value = '';
        }
    };

    const handleDeleteImage = (image) => {
        const usageCount = image.usages?.length || 1;
        const confirmed = window.confirm(
            `Delete this image from ${usageCount} site ${
                usageCount === 1 ? 'placement' : 'placements'
            }? The uploaded asset will remain available in the media library.`
        );
        if (!confirmed) return;

        setDeletedImageIds((current) =>
            current.includes(image.id) ? current : [...current, image.id]
        );
        setReplacements((current) => {
            const next = { ...current };
            delete next[image.id];
            return next;
        });
        if (editingFilenameId === image.id) cancelFilenameEdit();
    };

    const startFilenameEdit = (image) => {
        const filename = filenameDrafts[image.id] || image.label;
        setEditingFilenameId(image.id);
        setFilenameInput(splitFilenameParts(filename).name);
    };

    const cancelFilenameEdit = () => {
        setEditingFilenameId(null);
        setFilenameInput('');
    };

    const commitFilenameEdit = (image) => {
        const currentFilename = filenameDrafts[image.id] || image.label;
        const { extension } = splitFilenameParts(currentFilename);
        const nextFilename = buildFilename(filenameInput, extension);
        if (!nextFilename) {
            notifications.push({
                title: 'Enter an image filename',
                variant: 'error',
            });
            return;
        }
        setFilenameDrafts((current) => ({
            ...current,
            [image.id]: nextFilename,
        }));
        cancelFilenameEdit();
    };

    const handleSave = async () => {
        if (isSaving) return;

        const filenamesToSave = { ...filenameDrafts };
        if (editingFilenameId) {
            const editingImage = images.find(
                (image) => image.id === editingFilenameId
            );
            if (editingImage) {
                const currentFilename =
                    filenameDrafts[editingImage.id] || editingImage.label;
                const { extension } = splitFilenameParts(currentFilename);
                const nextFilename = buildFilename(filenameInput, extension);
                if (!nextFilename) {
                    notifications.push({
                        title: 'Enter an image filename',
                        variant: 'error',
                    });
                    return;
                }
                filenamesToSave[editingImage.id] = nextFilename;
            }
        }

        setIsSaving(true);
        notifications.push({
            title: 'Saving SEO changes…',
            message: 'You can continue editing while the changes are saved.',
            variant: 'info',
        });
        onClose();

        try {
            const effectiveReplacements = { ...replacements };
            for (const image of images) {
                if (deletedImageIds.includes(image.id)) continue;
                const requestedFilename = String(
                    filenamesToSave[image.id] || image.label
                ).trim();
                const currentReplacement = effectiveReplacements[image.id];
                const currentUrl = currentReplacement?.url || image.url;
                const currentFilename = String(
                    currentReplacement?.filename || image.label
                ).trim();
                if (
                    !requestedFilename ||
                    requestedFilename === currentFilename
                ) {
                    continue;
                }

                const renamedImage = await renameImageAsset({
                    companyId: editor.global.data.company_id,
                    currentFilename,
                    currentUrl,
                    nextFilename: requestedFilename,
                });
                effectiveReplacements[image.id] = {
                    ...currentReplacement,
                    ...renamedImage,
                    filename: renamedImage?.filename || requestedFilename,
                    url: renamedImage?.url || currentUrl,
                    sizeBytes:
                        currentReplacement?.sizeBytes ??
                        imageSizesByUrl[image.url] ??
                        0,
                };

                const libraryImage = editor.site.data?.images?.find(
                    (candidate) =>
                        String(candidate?.url || '').trim() === image.url
                );
                if (libraryImage?.image_id) {
                    editor.site.updateImage?.({
                        image_id: libraryImage.image_id,
                        data: {
                            filename:
                                renamedImage?.filename || requestedFilename,
                            url: renamedImage?.url || currentUrl,
                        },
                    });
                }
            }

            const altUpdatedSources = applyAltTextDrafts(
                sources,
                images,
                drafts,
                decorativeDrafts
            );
            const replacedSources = applyImageReplacements(
                altUpdatedSources,
                images,
                effectiveReplacements
            );
            const updatedSources = applyImageDeletions(
                replacedSources,
                images,
                deletedImageIds
            );
            const changedSources = updatedSources.filter(
                (source) => source.changed
            );
            const sourcesByPage = new Map();

            changedSources.forEach((source) => {
                const pageName = source.global ? 'index' : source.pageName;
                const group = sourcesByPage.get(pageName) || [];
                group.push(source);
                sourcesByPage.set(pageName, group);
            });

            if (sourcesByPage.size) {
                const pageUpdates = Array.from(sourcesByPage.entries()).map(
                    ([pageName, pageSources]) => ({
                        page_name: pageName,
                        components_to_update: pageSources.map((source) => ({
                            name: source.componentName,
                            data_file: source.dataFile,
                            props: source.props,
                        })),
                    })
                );

                try {
                    await API.patch(
                        `${editor.global.data.company_id}/pages/bulk`,
                        { pages: pageUpdates }
                    );
                } catch (error) {
                    if (
                        !(
                            error instanceof APIRequestError &&
                            error.status === 404
                        )
                    ) {
                        throw error;
                    }

                    // Older Editor API deployments do not have the bulk route.
                    // Preserve compatibility until every environment is updated.
                    for (const page of pageUpdates) {
                        await API.patch(
                            `${editor.global.data.company_id}/pages`,
                            {
                                page_name: page.page_name,
                                data: {
                                    components_to_update:
                                        page.components_to_update,
                                    components_to_delete: [],
                                },
                            }
                        );
                    }
                }
            }

            const currentPageName = editor.page.name || 'index';
            const currentUpdates = changedSources
                .filter(
                    (source) =>
                        source.global || source.pageName === currentPageName
                )
                .map((source) => {
                    const currentComponent = editor.components.data.find(
                        (component) => component.data_file === source.dataFile
                    );
                    return {
                        file: source.dataFile,
                        props: {
                            ...source.props,
                            ...(currentComponent?.props?.company
                                ? { company: currentComponent.props.company }
                                : {}),
                            ...(currentComponent?.props?.brand
                                ? { brand: currentComponent.props.brand }
                                : {}),
                        },
                    };
                });
            editor.components.commitProps?.(currentUpdates);
            onSourcesSaved?.(
                updatedSources.map((source) => {
                    const savedSource = { ...source };
                    delete savedSource.changed;
                    return savedSource;
                })
            );
            Object.entries(effectiveReplacements).forEach(
                ([imageId, replacement]) => {
                    if (deletedImageIds.includes(imageId)) return;
                    onImageSizeChange?.(
                        imageId,
                        replacement.url,
                        replacement.sizeBytes
                    );
                }
            );
            notifications.push({
                title: 'SEO changes saved!',
                message:
                    'Image filenames and descriptions were updated across the site.',
                variant: 'success',
                ttl: 10,
            });
        } catch (error) {
            notifications.push({
                title: 'Failed to save SEO changes',
                variant: 'error',
                error,
            });
        } finally {
            setIsSaving(false);
        }
    };

    const statusCounts = activeImages.reduce(
        (counts, image) => {
            const status = imageStatusById.get(image.id);
            if (status?.isMissing) counts.missing += 1;
            if (status?.isHeavy) counts.heavy += 1;
            if (status?.qualityIssues?.length) counts.quality += 1;
            return counts;
        },
        {
            missing: 0,
            heavy: 0,
            quality: 0,
        }
    );
    const missingCount = statusCounts.missing;
    const heavyCount = statusCounts.heavy;
    const qualityCount = statusCounts.quality;

    return (
        <EditorDrawer
            storageKey="altTextEditorDrawer"
            title="SEO Optimizations"
            open={open}
            onClose={handleCancel}
            onCancel={handleCancel}
            onSave={handleSave}
        >
            <div className="space-y-5 py-5">
                <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
                    <div>
                        {missingCount > 0 && (
                            <p className="text-xs font-medium text-amber-700">
                                {missingCount}{' '}
                                {missingCount === 1
                                    ? 'image needs'
                                    : 'images need'}{' '}
                                alt text.
                            </p>
                        )}
                        {heavyCount > 0 && (
                            <p className="mt-1 text-xs font-medium text-red-700">
                                {heavyCount}{' '}
                                {heavyCount === 1
                                    ? 'image is too heavy.'
                                    : 'images are too heavy.'}
                            </p>
                        )}
                        {qualityCount > 0 && (
                            <p className="mt-1 text-xs font-medium text-violet-700">
                                {qualityCount}{' '}
                                {qualityCount === 1
                                    ? 'image has'
                                    : 'images have'}{' '}
                                an alt-text quality warning.
                            </p>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={handleGenerateAllAltText}
                            disabled={
                                isGeneratingAllAltText ||
                                generatingImageIds.length > 0 ||
                                activeImages.length === 0
                            }
                            className="inline-flex items-center rounded-md bg-primary-700 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400"
                            title="Generate alt text for all non-decorative images"
                        >
                            <SparklesIcon
                                className={`mr-1.5 h-4 w-4 ${
                                    isGeneratingAllAltText
                                        ? 'animate-pulse'
                                        : ''
                                }`}
                            />
                            {isGeneratingAllAltText
                                ? `Generating ${generatingImageIds.length}…`
                                : 'Generate all alt text'}
                        </button>
                        <button
                            type="button"
                            disabled
                            className="inline-flex cursor-not-allowed items-center rounded-md bg-neutral-100 px-3 py-2 text-xs font-semibold text-neutral-400"
                            title="Bulk image compression is coming soon"
                        >
                            <i
                                className="fa-solid fa-arrow-down-to-line mr-1.5"
                                aria-hidden="true"
                            />
                            Compress all images
                        </button>
                    </div>
                </div>

                {activeImages.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-neutral-300 bg-white p-8 text-center text-sm text-neutral-500">
                        No editable images are used on this site.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {displayImages.map((image) => {
                            const status = imageStatusById.get(image.id);
                            const value = status?.value ?? '';
                            const isMissing = Boolean(status?.isMissing);
                            const decorative = Boolean(status?.decorative);
                            const qualityIssues = status?.qualityIssues || [];
                            const replacement = replacements[image.id];
                            const displayUrl = replacement?.url || image.url;
                            const sizeBytes = status?.sizeBytes ?? 0;
                            const sizeBadge = getImageSizeBadgeState(null, {
                                sizeBytes,
                            });
                            const isHeavy = Boolean(status?.isHeavy);
                            const isFirstHeavy =
                                isHeavy &&
                                !displayImages
                                    .slice(0, displayImages.indexOf(image))
                                    .some(
                                        (candidate) =>
                                            imageStatusById.get(candidate.id)
                                                ?.isHeavy
                                    );
                            const usageContexts = getUsageContexts(image);

                            return (
                                <div
                                    key={image.id}
                                    ref={
                                        isFirstHeavy ? firstHeavyImageRef : null
                                    }
                                    className={`grid gap-4 rounded-lg border bg-white p-3 shadow-sm sm:grid-cols-[112px_minmax(0,1fr)] ${
                                        isMissing && isHeavy
                                            ? 'border-orange-400 ring-1 ring-orange-100'
                                            : isMissing
                                            ? 'border-amber-300 ring-1 ring-amber-100'
                                            : isHeavy
                                            ? 'border-red-300 ring-1 ring-red-100'
                                            : qualityIssues.length > 0
                                            ? 'border-violet-300 ring-1 ring-violet-100'
                                            : 'border-emerald-300 ring-1 ring-emerald-100'
                                    }`}
                                >
                                    <div className="self-start">
                                        <div className="relative h-24 overflow-hidden rounded-md bg-neutral-100">
                                            <img
                                                src={displayUrl}
                                                alt=""
                                                className="block h-full w-full object-cover"
                                            />
                                            {sizeBytes > 0 && (
                                                <span
                                                    className={`absolute bottom-1 right-1 rounded px-1.5 py-0.5 text-[9px] font-semibold shadow ${sizeBadge.className}`}
                                                    title={sizeBadge.title}
                                                >
                                                    {sizeBadge.label}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-2 grid grid-cols-2 gap-1">
                                            <label className="inline-flex cursor-pointer items-center justify-center rounded bg-primary-700 px-2 py-1.5 text-[10px] font-semibold leading-none text-white transition-colors hover:bg-primary-600">
                                                {uploadingImageId === image.id
                                                    ? 'Uploading…'
                                                    : 'Replace'}
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    disabled={
                                                        uploadingImageId !==
                                                        null
                                                    }
                                                    onChange={(event) =>
                                                        handleReplaceImage(
                                                            image,
                                                            event
                                                        )
                                                    }
                                                    className="sr-only"
                                                />
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleDeleteImage(image)
                                                }
                                                disabled={
                                                    uploadingImageId !== null
                                                }
                                                className="inline-flex items-center justify-center rounded border border-red-200 bg-white px-2 py-1.5 text-[10px] font-semibold leading-none text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                                                title="Delete image from the site"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                    <div className="min-w-0">
                                        <div className="mb-2 flex w-full items-center gap-2">
                                            <div className="flex w-full min-w-0 flex-1 items-center gap-1">
                                                {editingFilenameId ===
                                                image.id ? (
                                                    <>
                                                        <div className="flex min-w-0 flex-1 items-center rounded-md border border-neutral-300 bg-white focus-within:border-neutral-500">
                                                            <input
                                                                autoFocus
                                                                type="text"
                                                                value={
                                                                    filenameInput
                                                                }
                                                                onChange={(
                                                                    event
                                                                ) =>
                                                                    setFilenameInput(
                                                                        event
                                                                            .target
                                                                            .value
                                                                    )
                                                                }
                                                                onKeyDown={(
                                                                    event
                                                                ) => {
                                                                    if (
                                                                        event.key ===
                                                                        'Enter'
                                                                    ) {
                                                                        event.preventDefault();
                                                                        commitFilenameEdit(
                                                                            image
                                                                        );
                                                                    }
                                                                    if (
                                                                        event.key ===
                                                                        'Escape'
                                                                    ) {
                                                                        event.preventDefault();
                                                                        cancelFilenameEdit();
                                                                    }
                                                                }}
                                                                aria-label={`Edit filename for ${image.label}`}
                                                                className="min-w-0 flex-1 border-0 bg-transparent px-2 py-1 text-xs text-neutral-700 focus:outline-none focus:ring-0"
                                                            />
                                                            <span className="pr-2 text-xs text-neutral-400">
                                                                {
                                                                    splitFilenameParts(
                                                                        filenameDrafts[
                                                                            image
                                                                                .id
                                                                        ] ||
                                                                            image.label
                                                                    ).extension
                                                                }
                                                            </span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                commitFilenameEdit(
                                                                    image
                                                                )
                                                            }
                                                            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-emerald-600 hover:bg-emerald-50"
                                                            title="Apply filename"
                                                            aria-label="Apply filename"
                                                        >
                                                            <CheckIcon className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={
                                                                cancelFilenameEdit
                                                            }
                                                            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-neutral-500 hover:bg-neutral-100"
                                                            title="Cancel filename edit"
                                                            aria-label="Cancel filename edit"
                                                        >
                                                            <XMarkIcon className="h-4 w-4" />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span
                                                            className="truncate text-xs font-medium text-neutral-700"
                                                            title={
                                                                filenameDrafts[
                                                                    image.id
                                                                ] || image.label
                                                            }
                                                        >
                                                            {filenameDrafts[
                                                                image.id
                                                            ] || image.label}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                startFilenameEdit(
                                                                    image
                                                                )
                                                            }
                                                            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-primary-700"
                                                            title="Edit image filename"
                                                            aria-label={`Edit filename for ${image.label}`}
                                                        >
                                                            <PencilSquareIcon className="h-4 w-4" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className="mb-2 flex flex-wrap gap-1">
                                            {isMissing && (
                                                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                                                    Missing Alt Text
                                                </span>
                                            )}
                                            {isHeavy && (
                                                <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">
                                                    Heavy File
                                                </span>
                                            )}
                                            {decorative && (
                                                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                                    Decorative
                                                </span>
                                            )}
                                        </div>
                                        <div className="mb-2 flex flex-wrap gap-1">
                                            {usageContexts.map((context) => (
                                                <span
                                                    key={context}
                                                    className="rounded bg-neutral-100 px-1.5 py-0.5 text-[9px] font-medium text-neutral-500"
                                                    title={`Used on ${context}`}
                                                >
                                                    {context}
                                                </span>
                                            ))}
                                        </div>
                                        <label className="block text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                                            Alt text
                                        </label>
                                        <div className="mt-1 rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                            <textarea
                                                rows={2}
                                                value={value}
                                                disabled={decorative}
                                                onChange={(event) =>
                                                    applyAltText(
                                                        image,
                                                        event.target.value
                                                    )
                                                }
                                                placeholder={
                                                    decorative
                                                        ? 'Decorative image — alt text will remain empty'
                                                        : 'Describe what is important in this image'
                                                }
                                                className="block min-h-[52px] w-full resize-y rounded-[10px] border-0 bg-white px-3 py-2 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-neutral-50"
                                            />
                                        </div>
                                        {qualityIssues.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-1">
                                                {qualityIssues.map((issue) => (
                                                    <span
                                                        key={issue.code}
                                                        className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700"
                                                    >
                                                        {issue.label}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <div className="mt-2 flex flex-wrap items-center justify-end gap-1.5">
                                            <label className="mr-auto inline-flex cursor-pointer items-center gap-1.5 text-[10px] font-semibold text-neutral-600">
                                                <input
                                                    type="checkbox"
                                                    checked={decorative}
                                                    onChange={(event) =>
                                                        setImageDecorative(
                                                            image,
                                                            event.target.checked
                                                        )
                                                    }
                                                    className="h-3.5 w-3.5 rounded border-neutral-300 text-primary-700 focus:ring-primary-500"
                                                />
                                                Mark as decorative
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleGenerateAltText(image)
                                                }
                                                disabled={
                                                    decorative ||
                                                    isGeneratingAllAltText ||
                                                    generatingImageIds.includes(
                                                        image.id
                                                    )
                                                }
                                                className="inline-flex h-6 w-6 items-center justify-center rounded bg-primary-700 text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400"
                                                title={
                                                    decorative
                                                        ? 'Decorative images do not need alt text'
                                                        : 'Generate alt text'
                                                }
                                                aria-label="Generate alt text"
                                            >
                                                <SparklesIcon
                                                    className={`h-3 w-3 ${
                                                        generatingImageIds.includes(
                                                            image.id
                                                        )
                                                            ? 'animate-pulse'
                                                            : ''
                                                    }`}
                                                />
                                            </button>
                                            <button
                                                type="button"
                                                disabled
                                                className="inline-flex h-6 w-6 cursor-not-allowed items-center justify-center rounded bg-neutral-100 text-neutral-400"
                                                title="Image compression is coming soon"
                                                aria-label="Compress image"
                                            >
                                                <i
                                                    className="fa-solid fa-arrow-down-to-line text-[10px]"
                                                    aria-hidden="true"
                                                />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {isSaving && (
                    <p className="text-right text-xs text-neutral-500">
                        Saving alt text…
                    </p>
                )}
            </div>
        </EditorDrawer>
    );
}
